import { useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type { SiteQueryContainerQuery } from '../__generated__/SiteQueryContainerQuery.graphql';
import type { VisitorInfoType } from '../types/visitorInfo';
import { getStorageKey } from '../types/visitorInfo';
import type { ILocationDescriptor } from './FundsService';
import Site from './Site';

/** Maps the route-level visitorInfoType to the GraphQL SpecialPage enum value. */
function getSpecialPageId(type: VisitorInfoType): 'VISITOR_INFO' | 'PUBLICATIONS_VISITOR_INFO' {
  if (type === 'publications') {
    return 'PUBLICATIONS_VISITOR_INFO';
  }
  return 'VISITOR_INFO';
}

// Re-export the SiteQuery for use in routing
export const SiteQuery = graphql`
    query SiteQueryContainerQuery($path: String!, $visitorInfo: VisitorInfo, $language: String!, $salesAdmission: String!, $searchString: String, $specialPageId: SpecialPage!) {
        site {
            ...Site_site @arguments(path: $path, visitorInfo: $visitorInfo, language: $language, salesAdmission: $salesAdmission, searchString: $searchString, specialPageId: $specialPageId)
        }
        user {
            ...Site_user
        }
    }
`;

interface VisitorInfoDataType {
  country: 'at' | 'de' | null;
  code: 'private' | 'business';
}

interface SiteQueryContainerProps {
  routeProps: {
    location: ILocationDescriptor;
    params?: Record<string, string>;
  };
  lang: string;
  visitorInfoType: VisitorInfoType;
  needsNavigation: boolean;
  isIndex?: boolean;
}

/**
 * Container component that fetches the root Site query using useLazyLoadQuery.
 * This replaces the legacy QueryRenderer pattern with modern Relay hooks.
 *
 * The component:
 * 1. Computes query variables from route props
 * 2. Fetches data using useLazyLoadQuery (suspends while loading)
 * 3. Passes fetched data to the Site component
 */
export function SiteQueryContainer({ routeProps, lang, visitorInfoType, needsNavigation, isIndex = false }: SiteQueryContainerProps): React.ReactElement {
  // Always read the fund-route visitor info from sessionStorage for the GraphQL
  // query variable. The server uses this to decide page access (e.g., returning
  // FundsDetailsPage vs VisitorInfoPage). This must be sent regardless of
  // visitorInfoType because routes like `:path` (visitorInfoType="none") can
  // still resolve to fund pages on the server (e.g., /AT0000A1TVM9).
  const queryVisitorInfo = useMemo<VisitorInfoDataType | null>(() => {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const stored = sessionStorage.getItem('visitorInfo');
    return stored ? JSON.parse(stored) : null;
  }, [routeProps.location.pathname]);

  // Read the route-specific visitor info for the gating decision (whether to
  // show the disclaimer form). For "funds" routes this reads from 'visitorInfo',
  // for "publications" routes from 'publicationsVisitorInfo', for "none" it's null.
  const gatingVisitorInfo = useMemo<VisitorInfoDataType | null>(() => {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const storageKey = getStorageKey(visitorInfoType);
    if (!storageKey) {
      return null;
    }
    const stored = sessionStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : null;
  }, [routeProps.location.pathname, visitorInfoType]);

  // Compute query variables from route props
  const variables = useMemo(() => {
    let path = routeProps.location.pathname.replace(/^\/|\/$/g, '');

    // For index routes, append 'Front' to the path
    if (isIndex) {
      // If path is empty (root URL), use just 'Front'
      // If path has content (e.g., 'de'), use path/Front
      path = path.length > 0 ? `${path}/Front` : 'Front';
    }

    const language = lang.replace(/^\/|\/$/g, '') || 'de';

    return {
      path,
      visitorInfo: queryVisitorInfo,
      language,
      salesAdmission: (queryVisitorInfo?.country || 'at').toUpperCase(),
      searchString: String(routeProps.location.query?.search || ''),
      specialPageId: getSpecialPageId(visitorInfoType),
    };
  }, [routeProps.location.pathname, routeProps.location.query?.search, isIndex, queryVisitorInfo, lang, visitorInfoType]);

  // Fetch data using useLazyLoadQuery.
  // 'store-and-network' renders cached data immediately (no Suspense flicker)
  // while re-fetching in the background. This prevents the brief flash of the
  // loading fallback when navigating between pages (e.g., funds list → fund detail).
  const data = useLazyLoadQuery<SiteQueryContainerQuery>(SiteQuery, variables, {
    fetchPolicy: 'store-and-network',
  });

  // Handle null data - this shouldn't happen if the query succeeds
  if (!data.site || !data.user) {
    // biome-ignore lint/suspicious/noConsole: This is an error message
    console.error('[SiteQueryContainer] Missing data - site:', data.site, 'user:', data.user);
    // Import ErrorPage dynamically to avoid circular dependency
    const ErrorPage = require('../page/errorPage').default;
    return <ErrorPage error={{ statusCode: 500, message: 'Failed to load site data' }} />;
  }

  // Render Site component with fetched data
  return <Site site={data.site} user={data.user} location={routeProps.location} visitorInfo={gatingVisitorInfo} visitorInfoType={visitorInfoType} needsNavigation={needsNavigation} lang={variables.language} />;
}

export default SiteQueryContainer;
